/**
 * "N escolhe, e o app lembra." Todos os vínculos aparecem, inclusive os cujo
 * portal ainda mora no app atual (A007) — o cartão diz onde.
 */
import { Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBriefcase, IconBuilding, IconHeart, IconStethoscope, IconUserShield } from '@tabler/icons-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { EVA_BRAND_ID, type BrandRole } from '@eva/schemas';
import { contextKey, portalFor, ROLE_LABELS, useSession, type AvailableContext, useBrand } from '@eva/domain';
import { ContextCard } from '@eva/ui';
import { caminhoDeSaida } from '../app/sair';
import { AuthFrame } from './AuthFrame';
import { destinoDepoisDoLogin, peekRedirect } from '../app/guards';

function iconeDe(role: BrandRole) {
  switch (role) {
    case 'PROVIDER':
      return <IconStethoscope size={22} />;
    case 'COMPANY_MANAGER':
      return <IconBuilding size={22} />;
    case 'MAINTAINER':
      return <IconUserShield size={22} />;
    case 'BRAND_ADMIN':
    case 'BRAND_CRM':
    case 'BRAND_CMS':
      return <IconBriefcase size={22} />;
    default:
      return <IconHeart size={22} />;
  }
}

function nomeDaMarca(c: AvailableContext): string {
  // Mantenedora não tem marca: o que ela governa é o ecossistema (A044).
  if (c.role === 'MAINTAINER') return 'Ecossistema';
  return c.brandName ?? (c.brandId === EVA_BRAND_ID ? 'Eva' : c.brandId ?? '');
}

export function ContextChooserPage() {
  const { brand } = useBrand();
  const session = useSession();
  const navigate = useNavigate();
  /*
   * Desmarcado por padrão.
   *
   * Marcado, a escolha era gravada sem ninguém pedir — e quem tem mais de um
   * contexto normalmente alterna entre eles. Guardar preferência no aparelho é
   * opt-in: quem quer, marca.
   */
  const [lembrar, setLembrar] = useState(false);
  const [assumindo, setAssumindo] = useState<string | null>(null);

  if (session.status === 'anonymous') return <Navigate to="/entrar" replace />;
  if (session.status === 'ready' && session.contexts.length <= 1) return <Navigate to="/" replace />;
  // Quem entrou para autorizar uma IA não precisa escolher contexto: o
  // consentimento pergunta a marca ele mesmo, e o pedido expira em 10 min.
  if (peekRedirect()?.startsWith('/oauth/consentimento')) return <Navigate to={peekRedirect()!} replace />;

  const escolher = async (ctx: AvailableContext) => {
    setAssumindo(contextKey(ctx));
    try {
      await session.assume(ctx, { remember: lembrar });
      navigate(destinoDepoisDoLogin(ctx.role), { replace: true });
    } catch (e: any) {
      notifications.show({ color: 'red', title: 'Não foi possível entrar', message: e?.message ?? 'Tente novamente.' });
    } finally {
      setAssumindo(null);
    }
  };

  const nome = session.user?.preferredName || session.user?.fullName?.split(' ')[0] || '';

  return (
    <AuthFrame title={nome ? `Olá, ${nome}` : 'Como você quer entrar?'} subtitle="Escolha como quer entrar.">
      <Stack gap="sm">
        {session.contexts.map((c) => {
          const portal = portalFor(c.role);
          return (
            <ContextCard
              key={contextKey(c)}
              brandName={nomeDaMarca(c)}
              roleLabel={ROLE_LABELS[c.role]}
              detail={c.companyName ?? null}
              icon={iconeDe(c.role)}
              disabled={assumindo !== null && assumindo !== contextKey(c)}
              hint={portal.available ? null : 'Ainda no app atual — vamos te levar até lá'}
              onClick={() => escolher(c)}
            />
          );
        })}
        <Checkbox
          mt="xs"
          checked={lembrar}
          onChange={(e) => setLembrar(e.currentTarget.checked)}
          label="Lembrar minha escolha neste aparelho"
        />
        <Group justify="center" mt="md">
          <Button variant="subtle" color="gray" onClick={() => session.logout().then(() => navigate(caminhoDeSaida(brand.slug), { replace: true }))}>
            Sair
          </Button>
        </Group>
        {session.user && (
          <Text fz="xs" c="dimmed" ta="center">{session.user.email}</Text>
        )}
      </Stack>
    </AuthFrame>
  );
}
